import { useSetAtom } from 'jotai';
import React, { useCallback } from 'react';

import { Chip, Name, Table, Text } from '~/components/blocks';
import { ContactMethod } from '~/components/pages/Patient/AllPatientList/AllPatient/ContactMethod';
import { useMedicationFollowupSettingStatus } from '~/components/partials/PatientDetailDialog/use-medication-followup-setting-status';
import { PatientOnAllPatientListFragment } from '~/graphql/generated/graphql';
import { patientDetailDialogState } from '~/state/partials/patient_detail_dialog/atoms';
import { TabType } from '~/state/partials/patient_detail_dialog/types';
import { Label } from '~/utils/label';

import { CommentColumn } from './CommentColumn';
import { MessageSendAtColumn } from './MessageSendAtColumn';
import { MessageSendFromColumn } from './MessageSendFromColumn';
import { QuestionnaireSheetColumn } from './QuestionnaireSheetColumn';

type Props = {
  patient: PatientOnAllPatientListFragment;
};

export const Patient = React.memo(({ patient }: Props) => {
  const now = new Date();
  const latestFollowupMessage =
    patient.medicationFollowupMessages.nodes.find((m) => !m.isAutoReplyMessage) || null;

  // すでに送信されたメッセージの最新を取得する
  const latestSentFollowupMessage =
    patient.medicationFollowupMessages.nodes.find(
      (m) => !m.isAutoReplyMessage && m.status === 'sent' && new Date(m.sendAt) < now,
    ) || null;
  const latestSentFollowupAutoReplyMessage =
    patient.medicationFollowupMessages.nodes.find(
      (m) => m.isAutoReplyMessage && m.status === 'sent' && new Date(m.sendAt) < now,
    ) || null;

  const { statusManagement } = useMedicationFollowupSettingStatus();
  const medixsPatient = patient.medixsPatient;
  const medicationFollowupStatus =
    medixsPatient?.medicationFollowupStatus || patient.medicationFollowupStatus;
  const isFollowingUp = medicationFollowupStatus === 'in_progress';
  const guestPatient = patient.medixsPatient?.guestPatients.filter(
    (gp) => gp.__typename !== 'LineVisitor',
  )[0];
  const setPatientDetailDialogState = useSetAtom(patientDetailDialogState);

  const handleClick = useCallback(() => {
    setPatientDetailDialogState((_state) => ({
      ..._state,
      isOpen: true,
      type: 'message' as TabType,
      patientId: patient.id,
    }));
  }, [setPatientDetailDialogState, patient.id]);

  const hasLineVisitor = medixsPatient?.guestPatients.some(
    (guestPatient) => guestPatient.__typename === 'LineVisitor' && guestPatient.active,
  );

  return (
    <Table.Tr onClick={handleClick}>
      <Table.Td>
        <Name
          familyName={patient.familyName}
          givenName={patient.givenName}
          phoneticFamilyName={patient.phoneticFamilyName}
          phoneticGivenName={patient.phoneticGivenName}
        />
      </Table.Td>
      <Table.Td>
        <Text size="m" whiteSpace="nowrap">
          {Label.age(patient.birthDate)}({Label.sex(patient.sex)})
        </Text>
      </Table.Td>
      <Table.Td>
        <ContactMethod
          hasApp={true}
          phoneNumber={guestPatient?.mobilePhone}
          hasLine={hasLineVisitor}
        />
      </Table.Td>
      <Table.Td>
        <MessageSendAtColumn latestFollowupMessage={latestFollowupMessage} />
      </Table.Td>
      <Table.Td>
        {statusManagement && isFollowingUp && (
          <Chip size="fixed" color="blue" textColor="white">
            対応中
          </Chip>
        )}
      </Table.Td>
      <Table.Td>
        <QuestionnaireSheetColumn
          latestSentFollowupMessage={latestSentFollowupMessage}
          latestSentFollowupAutoReplyMessage={latestSentFollowupAutoReplyMessage}
        />
      </Table.Td>
      <Table.Td>
        <CommentColumn patient={patient} />
      </Table.Td>
      <Table.Td>
        <MessageSendFromColumn latestFollowupMessage={latestFollowupMessage} />
      </Table.Td>
    </Table.Tr>
  );
});

Patient.displayName = 'Patient';
